import { apiErrorMessage } from '@agos/api-client'
import {
  useDeleteSmsContactsContactId,
  useGetSmsContacts,
  usePostSmsContacts,
  usePutSmsContactsContactId,
} from '@agos/api-client/customers'
import { T, formatDay, formatPhone, tt } from '@agos/shared'
import { ConfirmDialog, DataTable, useToast, type Column } from '@agos/ui'
import { Check, Pencil, Plus, Search, Trash2, X } from 'lucide-react'
import { useState } from 'react'

import {
  CONTACTS_PAGE,
  contactPayload,
  contactProblemNote,
  contactProblems,
  contactsOf,
  maySaveContact,
  pagerNote,
  totalOf,
  type SmsContactRow,
} from './sms-contacts'

/**
 * The SMS contact book — §9.241, FB-401.
 *
 * Ported from legacy `SmsPhoneDb` (`loyalty-program.tsx:4055`), which legacy renders at the foot of
 * its SMS tab and AGOS had never rendered anywhere. Four endpoints, **20 rows of the owner's data**,
 * and no screen.
 *
 * **This sends nothing.** `POST /sms-contacts/bulk-sms` is on `BLOCKED.md` and is not reachable from
 * here: the campaign composer is a different control on a different tab. The separation is the point
 * — a phone book and a send button that look like one thing is how somebody texts two hundred people
 * by accident.
 */
export function SmsContactsPanel({ readOnly }: { readOnly: string | null }) {
  const { toast } = useToast()
  const [search, setSearch] = useState('')
  // `DataTable`'s server paging is 1-based; the endpoint's offset is not.
  const [page, setPage] = useState(1)
  const [adding, setAdding] = useState(false)
  const [editingId, setEditingId] = useState<number | null>(null)

  const list = useGetSmsContacts({
    search: search.trim() === '' ? undefined : search.trim(),
    limit: CONTACTS_PAGE,
    offset: (page - 1) * CONTACTS_PAGE,
  })
  const rows = contactsOf(list.data)
  const total = totalOf(list.data)

  const refresh = () => void list.refetch()
  // §9.395 - the error. The three `.catch(() => undefined)` chains below are **not** the handler:
  // these mutations carry `onError: failed`, so those exist only to stop an unhandled rejection, and
  // fixing this one fixes all three.
  const failed = (caught: unknown) =>
    toast({
      variant: 'destructive',
      title: tt('Nepodarilo sa uložiť'),
      description: apiErrorMessage(caught, tt('Server zmenu odmietol.')),
    })

  const create = usePostSmsContacts({ mutation: { onSuccess: refresh, onError: failed } })
  const update = usePutSmsContactsContactId({ mutation: { onSuccess: refresh, onError: failed } })
  const remove = useDeleteSmsContactsContactId({ mutation: { onSuccess: refresh, onError: failed } })
  const [removing, setRemoving] = useState<SmsContactRow | null>(null)

  const columns: Column<SmsContactRow>[] = [
    {
      key: 'name',
      header: tt('Meno'),
      render: (row) =>
        editingId === row.id ? (
          <EditableName row={row} onDone={() => setEditingId(null)} save={update} />
        ) : (
          <span data-contact-name>{row.name ?? <span className="text-muted-foreground">-</span>}</span>
        ),
      sortable: true,
      sortValue: (row) => row.name ?? '',
    },
    {
      key: 'phone',
      header: tt('Telefón'),
      // Grouped the way every other screen shows a number, from the same helper.
      render: (row) => <span data-contact-phone={row.phone}>{formatPhone(row.phone) || row.phone}</span>,
      sortable: true,
      sortValue: (row) => row.phone,
    },
    {
      key: 'created',
      header: tt('Pridaný'),
      render: (row) => (row.createdAt === null ? '-' : formatDay(row.createdAt)),
      sortable: true,
      sortValue: (row) => row.createdAt ?? '',
    },
    {
      key: 'actions',
      header: '',
      className: 'text-right',
      render: (row) =>
        readOnly !== null ? null : (
          <div className="flex justify-end gap-1">
            <button
              type="button"
              data-contact-edit={row.id}
              aria-label={tt('Upraviť kontakt')}
              onClick={() => setEditingId(editingId === row.id ? null : row.id)}
              className="p-1.5 rounded-lg hover:bg-muted"
            >
              <Pencil className="w-3.5 h-3.5" />
            </button>
            <button
              type="button"
              data-contact-delete={row.id}
              aria-label={tt('Zmazať kontakt')}
              onClick={() => setRemoving(row)}
              className="p-1.5 rounded-lg hover:bg-muted text-red-500"
            >
              <Trash2 className="w-3.5 h-3.5" />
            </button>
          </div>
        ),
    },
  ]

  return (
    <section
      className="bg-card border border-border rounded-2xl overflow-hidden"
      data-sms-contacts
    >
      <div className="px-5 py-4 border-b border-border flex items-center justify-between gap-3 flex-wrap">
        <div>
          <h2 className="text-xs font-bold text-muted-foreground uppercase tracking-wider">
            <T>SMS kontakty</T>
          </h2>
          {/* Said here rather than left to be inferred: this list is the audience of an SMS
            * campaign, which is what makes a wrong number on it worth removing. */}
          <p className="text-[11px] text-muted-foreground mt-0.5">
            <T>Zoznam čísel, ktorým odchádza hromadná SMS kampaň.</T>
          </p>
        </div>
        <div className="flex items-center gap-2">
          <div className="relative">
            <Search className="w-3.5 h-3.5 absolute left-2.5 top-1/2 -translate-y-1/2 text-muted-foreground" />
            <input
              type="search"
              data-contact-search
              value={search}
              placeholder={tt('Hľadať meno alebo číslo…')}
              onChange={(event) => {
                setSearch(event.target.value)
                // A narrower list has fewer pages; staying on page 9 would show an empty one.
                setPage(1)
              }}
              className="pl-8 pr-3 py-1.5 rounded-xl border border-border bg-background text-xs w-56"
            />
          </div>
          {readOnly === null && (
            <button
              type="button"
              data-contact-add-open
              onClick={() => setAdding((open) => !open)}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-xl border border-border text-xs font-semibold"
            >
              <Plus className="w-3.5 h-3.5" />
              <T>Pridať kontakt</T>
            </button>
          )}
        </div>
      </div>

      {adding && readOnly === null && (
        <AddContact
          saving={create.isPending}
          onCancel={() => setAdding(false)}
          onSave={(name, phone) => {
            void create
              .mutateAsync({ data: contactPayload(name, phone) })
              .then(() => setAdding(false))
              .catch(() => undefined)
          }}
        />
      )}

      <div className="px-2 py-2">
        <DataTable
          columns={columns}
          data={rows}
          rowKey={(row) => String(row.id)}
          rowAttributes={(row) => ({ 'data-contact-row': String(row.id) })}
          emptyMessage={tt('Žiadne SMS kontakty')}
          // **Server paging, not client.** The endpoint answers fifty and a total; slicing the fifty
          // it sent would page a page.
          page={page}
          totalItems={total}
          pageSize={CONTACTS_PAGE}
          onPageChange={setPage}
          compact
        />
        <p className="text-[11px] text-muted-foreground px-3 pb-1" data-contact-total={total}>
          {pagerNote(total, page - 1)}
        </p>
      </div>

      <ConfirmDialog
        open={removing !== null}
        onClose={() => setRemoving(null)}
        onConfirm={() => {
          const target = removing
          if (target === null) return
          void remove
            .mutateAsync({ contactId: target.id })
            .then(() => setRemoving(null))
            .catch(() => undefined)
        }}
        loading={remove.isPending}
        variant="danger"
        title={tt('Zmazať kontakt?')}
        description={tt('Číslo prestane dostávať hromadné SMS kampane.')}
        confirmLabel={tt('Zmazať')}
        cancelLabel={tt('Zrušiť')}
      />
    </section>
  )
}

function AddContact({
  saving,
  onSave,
  onCancel,
}: {
  saving: boolean
  onSave: (name: string, phone: string) => void
  onCancel: () => void
}) {
  const [name, setName] = useState('')
  const [phone, setPhone] = useState('')
  const problems = contactProblems(phone)

  return (
    <div className="px-5 py-4 border-b border-border space-y-2" data-contact-add>
      <div className="flex flex-wrap gap-2">
        <input
          data-contact-add-name
          value={name}
          placeholder={tt('Meno (voliteľné)')}
          onChange={(event) => setName(event.target.value)}
          className="flex-1 min-w-[10rem] px-3 py-1.5 rounded-xl border border-border bg-background text-xs"
        />
        <input
          data-contact-add-phone
          value={phone}
          placeholder={tt('Telefónne číslo')}
          onChange={(event) => setPhone(event.target.value)}
          className="flex-1 min-w-[10rem] px-3 py-1.5 rounded-xl border border-border bg-background text-xs"
        />
        <button
          type="button"
          data-contact-add-save
          disabled={saving || !maySaveContact(phone)}
          onClick={() => onSave(name, phone)}
          className="px-3 py-1.5 rounded-xl bg-primary text-primary-foreground text-xs font-bold disabled:opacity-50"
        >
          <T>Uložiť</T>
        </button>
        <button
          type="button"
          data-contact-add-cancel
          onClick={onCancel}
          className="px-3 py-1.5 rounded-xl border border-border text-xs font-semibold"
        >
          <T>Zrušiť</T>
        </button>
      </div>
      {/* Only once something has been typed: an empty form is not yet a mistake. */}
      {phone !== '' &&
        problems.map((problem) => (
          <p key={problem} className="text-[11px] text-amber-500" data-contact-problem={problem}>
            {contactProblemNote(problem)}
          </p>
        ))}
    </div>
  )
}

function EditableName({
  row,
  onDone,
  save,
}: {
  row: SmsContactRow
  onDone: () => void
  save: ReturnType<typeof usePutSmsContactsContactId>
}) {
  const [name, setName] = useState(row.name ?? '')
  const [phone, setPhone] = useState(row.phone)

  return (
    <div className="flex items-center gap-1" data-contact-editing={row.id}>
      <input
        data-contact-edit-name
        value={name}
        onChange={(event) => setName(event.target.value)}
        className="px-2 py-1 rounded-lg border border-border bg-background text-xs w-28"
      />
      <input
        data-contact-edit-phone
        value={phone}
        onChange={(event) => setPhone(event.target.value)}
        className="px-2 py-1 rounded-lg border border-border bg-background text-xs w-32"
      />
      <button
        type="button"
        data-contact-edit-save
        aria-label={tt('Uložiť kontakt')}
        disabled={save.isPending || !maySaveContact(phone)}
        onClick={() => {
          void save
            .mutateAsync({ contactId: row.id, data: contactPayload(name, phone) })
            .then(onDone)
            .catch(() => undefined)
        }}
        className="p-1.5 rounded-lg hover:bg-muted text-emerald-600 disabled:opacity-40"
      >
        <Check className="w-3.5 h-3.5" />
      </button>
      <button
        type="button"
        data-contact-edit-cancel
        aria-label={tt('Zrušiť úpravu')}
        onClick={onDone}
        className="p-1.5 rounded-lg hover:bg-muted"
      >
        <X className="w-3.5 h-3.5" />
      </button>
    </div>
  )
}
